/**
 * Shared SSE (Server-Sent Events) stream consumer.
 *
 * Reads a streaming Response body and parses SSE `data:` lines,
 * dispatching tokens and completion events to the provided callbacks.
 */
export async function consumeSSE(
  response: Response,
  onToken: (text: string) => void,
  onDone: (output: string, metadata?: Record<string, unknown>) => void,
  onError: (message: string) => void,
): Promise<void> {
  if (!response.body) {
    onError('Response body is null');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullOutput = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') continue;

        try {
          const parsed = JSON.parse(data);
          if (parsed.error) {
            onError(parsed.error);
            return;
          }
          if (parsed.text) {
            fullOutput += parsed.text;
            onToken(parsed.text);
          }
          if (parsed.done) {
            onDone(parsed.output ?? fullOutput, parsed.metadata);
            return;
          }
        } catch {
          // Skip malformed SSE data lines
        }
      }
    }

    // Stream ended without explicit done event — use accumulated text
    if (fullOutput) {
      onDone(fullOutput);
    }
  } catch (err) {
    onError(err instanceof Error ? err.message : 'SSE stream error');
  }
}
